import { useEffect, useState } from "react";
import { Upload, Plus,FileText, Download, Trash2, Search } from "lucide-react";

import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogTitle
} from "./ui/dialog";

import { DocumentRepository } from "@/infrastructure/repositories/DocumentRepository";
import { PracticeRepository } from "@/infrastructure/repositories/PracticeRepository";

const practiceRepo = new PracticeRepository();

const documentRepo = new DocumentRepository();

export function AIKnowledgeContent() {
  const userId = localStorage.getItem("current_user_swimmer_id") || "";

  const PAGE_SIZE = 5;

  const [knowledgeFiles, setKnowledgeFiles] = useState<any[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<any[]>([]);
  const [paginatedFiles, setPaginatedFiles] = useState<any[]>([]);
  const [practices, setPractices] = useState<any[]>([]);
  const [practiceDialogOpen, setPracticeDialogOpen] = useState(false);
  const [laps, setLaps] = useState<any[]>([]);
  const [selectedPracticeIds, setSelectedPracticeIds] = useState<string[]>([]);

  const [isLoadingFiles, setIsLoadingFiles] = useState(true); // loader state

  const [searchQuery, setSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredFiles.length / PAGE_SIZE);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [cancelUpload, setCancelUpload] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStatus, setDialogStatus] = useState<
    "success" | "error" | "loading" | null
  >(null);
  const [dialogMessageTitle, setDialogMessageTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Search filter
  useEffect(() => {
    const query = searchQuery.toLowerCase();

    const result = knowledgeFiles.filter((file) => {
      return (
        file.name.toLowerCase().includes(query) ||
        file.type.toLowerCase().includes(query) ||
        file.uploaded.toLowerCase().includes(query)
      );
    });

    setFilteredFiles(result);
    setCurrentPage(1);
  }, [searchQuery, knowledgeFiles]);

  // Pagination slicing
  useEffect(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setPaginatedFiles(filteredFiles.slice(start, end));
  }, [currentPage, filteredFiles]);

  const fetchDocuments = async () => {
    try {
      setIsLoadingFiles(true);

      const docs = await documentRepo.getDocumentsByCoachId(userId, 1, 200);

      const formattedDocs = docs.map((doc: any) => ({
        id: doc.id,
        name: doc.name,
        size: "—",
        type: doc.type?.replace(".", "").toUpperCase(),
        uploaded: new Date(doc.updatedAt || doc.createdAt).toLocaleDateString(),
        url: doc.url
      }));

      setKnowledgeFiles(formattedDocs);
      setFilteredFiles(formattedDocs);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const startUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploadProgress(0);
    setCancelUpload(false);

    try {
      const simulateProgress = () =>
        new Promise<void>((resolve, reject) => {
          let progress = 0;

          const interval = setInterval(() => {
            if (cancelUpload) {
              clearInterval(interval);
              reject("cancelled");
              return;
            }

            progress += 10;
            setUploadProgress(progress);

            if (progress >= 100) {
              clearInterval(interval);
              resolve();
            }
          }, 150);
        });

      await simulateProgress();

      if (cancelUpload) return;

      for (const file of selectedFiles) {
        const { filePath } = await documentRepo.documentupload(file);
        await documentRepo.saveDocumentUploadedFile(file, filePath, userId);
      }

      await fetchDocuments();

      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadProgress(0);
    } catch (err) {
      setUploadProgress(0);
      setSelectedFiles([]);
    }
  };

  const handleDeleteDocument = (documentId: string) => {
    setDocumentToDelete(documentId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDocument = async () => {
    setDeleteConfirmOpen(false);

    setDialogStatus("loading");
    setDialogMessageTitle("Deleting...");
    setDialogMessage("Deleting your document...");
    setDialogOpen(true);

    try {
      if (documentToDelete) {
        await documentRepo.deleteDocument(documentToDelete, userId);
      }

      await fetchDocuments();

      setDialogStatus("success");
      setDialogMessageTitle("Delete Successful");
      setDialogMessage("Document deleted successfully!");
    } catch (error: any) {
      setDialogStatus("error");
      setDialogMessageTitle("Delete Failed");
      setDialogMessage(error.message || "Failed to delete document.");
    } finally {
      setDocumentToDelete(null);
    }
  };

  const handleDownloadDocument = (filePath: string, fileName: string) => {
    const baseUrl = "https://swimnerd-ai.azurewebsites.net";
    const fileUrl = `${baseUrl}/${filePath}`;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

const fetchPracticesForKnowledge = async () => {
  try {
    const { practices: rawPractices, laps } = await practiceRepo.getAllPractices(userId);

    const normalizedPractices = rawPractices.map(p => ({
      id: p.id,
      practice_id: p.practiceId,
      name: p.name,
      description: p.description,
      date: p.date,
      time: p.practice_time
    }));

    setPractices(normalizedPractices);
    setLaps(laps);
    setPracticeDialogOpen(true);
  } catch (err) {
    console.error("Failed to fetch practices", err);
  }
};

const sendJsonKnowledge = async () => {
  try {
    setDialogStatus("loading");
    setDialogMessageTitle("Saving...");
    setDialogMessage("Sending JSON knowledge...");
    setDialogOpen(true);

    const selected = practices.filter(p =>
      selectedPracticeIds.includes(p.id)
    );

    const jsonPayload = selected.map(practice => {
      const practiceLaps = laps.filter(
        lap => String(lap.practice_id) === String(practice.practice_id)
      );

      return {
        id: practice.id,
        practice_id: practice.practice_id,
        name: practice.name,
        description: practice.description,
        date: practice.date,
        time: practice.time,
        laps: practiceLaps.map(lap => ({
          id: lap.id,
          rep: lap.rep,
          distance: lap.distance,
          interval_lap: lap.interval_lap,
          energy_zone: lap.energy_zone,
          time: lap.time
        }))
      };
    });

    await documentRepo.addJsonKnowledge(userId, jsonPayload);
     
    await fetchDocuments();

    setDialogStatus("success");
    setDialogMessageTitle("Success");
    setDialogMessage("JSON file created with selected practices.");
  } catch (err: any) {
    setDialogStatus("error");
    setDialogMessageTitle("Failed");
    setDialogMessage(err.message || "Error sending JSON knowledge.");
  } finally {
    setSelectedPracticeIds([]);
    setPracticeDialogOpen(false);
  }
};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold">AI Knowledge Base</h1>
        <p className="text-muted-foreground">
          Manage training files and knowledge materials used by AI.
        </p>
      </div>

      <div className="flex space-x-3">
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Add Knowledge File
        </Button>

        <Button variant="outline" onClick={fetchPracticesForKnowledge}>
          <Plus className="h-4 w-4 mr-2" />
          Add Practice as Knowledge
        </Button>
      </div>
    </div>


      <div className="flex-1">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Knowledge Files</CardTitle>
        </CardHeader>

        <CardContent>
          {/* Loader while fetching */}
          {isLoadingFiles && (
            <div className="text-center py-10">
              <div className="animate-spin h-8 w-8 mx-auto border-2 border-primary border-t-transparent rounded-full"></div>
              <p className="mt-3 text-muted-foreground">Loading files...</p>
            </div>
          )}

          {/* Empty State */}
          {!isLoadingFiles && filteredFiles.length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No knowledge files found.</p>
            </div>
          )}

          {!isLoadingFiles && filteredFiles.length > 0 && (
            <div className="space-y-4">
              {paginatedFiles.map((file: any) => (
                <FileItem
                  key={file.id}
                  file={file}
                  onDelete={() => handleDeleteDocument(file.id)}
                  onDownload={() => handleDownloadDocument(file.url, file.name)}
                />
              ))}
            </div>
          )}

          {!isLoadingFiles && filteredFiles.length > PAGE_SIZE && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          )}
        </CardContent>
      </Card>

      <UploadDialog
        uploadDialogOpen={uploadDialogOpen}
        setUploadDialogOpen={setUploadDialogOpen}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        uploadProgress={uploadProgress}
        startUpload={startUpload}
        setCancelUpload={setCancelUpload}
      />

      <DeleteDialog
        deleteConfirmOpen={deleteConfirmOpen}
        setDeleteConfirmOpen={setDeleteConfirmOpen}
        confirmDelete={confirmDeleteDocument}
      />

      <StatusDialog
          dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        dialogStatus={dialogStatus}
        dialogMessageTitle={dialogMessageTitle}
        dialogMessage={dialogMessage}
      />

      <PracticeDialog
        practiceDialogOpen={practiceDialogOpen}
        setPracticeDialogOpen={setPracticeDialogOpen}
        practices={practices}
        selectedPracticeIds={selectedPracticeIds}
        setSelectedPracticeIds={setSelectedPracticeIds}
        sendJsonKnowledge={sendJsonKnowledge}
      />

    </div>
  );
}

function FileItem({ file, onDelete, onDownload }: any) {
  return (
    <div className="flex items-start space-x-4 p-4 border rounded-lg hover:shadow-md transition-shadow">
      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
        <FileText className="h-6 w-6 text-blue-600" />
      </div>

      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold">{file.name}</h3>

            <p className="text-sm text-muted-foreground mt-1">
              Uploaded on {file.uploaded}
            </p>

            <div className="flex items-center space-x-4 mt-2">
              <Badge variant="outline">{file.type}</Badge>
              <span className="text-sm text-muted-foreground">{file.size}</span>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={onDownload}>
              <Download className="h-4 w-4" />
            </Button>

            <Button variant="destructive" size="icon" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, setCurrentPage }: any) {
  return (
    <div className="flex justify-between items-center mt-6">
      <Button
        variant="outline"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
      >
        Previous
      </Button>

      <span className="text-sm text-muted-foreground">
        Page {currentPage} of {totalPages}
      </span>

      <Button
        variant="outline"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage((p: number) => Math.min(totalPages, p + 1))}
      >
        Next
      </Button>
    </div>
  );
}

function UploadDialog({
  uploadDialogOpen,
  setUploadDialogOpen,
  selectedFiles,
  setSelectedFiles,
  uploadProgress,
  startUpload,
  setCancelUpload
}: any) {
  return (
    <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogTitle>Upload Knowledge File</DialogTitle>

        <div className="space-y-4 mt-4">
          <input
            type="file"
            multiple
            accept=".pdf,.txt,.doc,.docx"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              setSelectedFiles(files);
            }}
          />
          </div>

          {uploadProgress > 0 && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>

              <Progress value={uploadProgress} className="h-3" />

              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setCancelUpload(true);
                    setSelectedFiles([]);
                    setUploadDialogOpen(false);
                  }}
                >
                  Cancel Upload
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            {!uploadProgress && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setUploadDialogOpen(false);
                    setSelectedFiles([]);
                  }}
                >
                  Cancel
                </Button>

                <Button onClick={startUpload} disabled={selectedFiles.length === 0}>
                  Upload
                </Button>
              </>
            )}
          </div>
        
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ deleteConfirmOpen, setDeleteConfirmOpen, confirmDelete }: any) {
  return (
    <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <DialogContent className="max-w-sm text-center">
        <DialogTitle className="sr-only">Confirm Delete</DialogTitle>

        <h2 className="text-lg font-semibold mb-2">Confirm Delete</h2>

        <p className="text-muted-foreground text-sm mb-6">
          Are you sure you want to delete this document?
        </p>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>

          <Button variant="destructive" onClick={confirmDelete}>
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({
  dialogOpen,
  setDialogOpen,
  dialogStatus,
  dialogMessageTitle,
  dialogMessage
}: any) {
  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="max-w-sm text-center">
        {dialogStatus === "loading" && (
          <div className="py-12">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        )}

        {dialogStatus === "success" && (
          <div className="py-6">
            <h2 className="text-lg font-semibold text-green-600 mb-2">
              {dialogMessageTitle}
            </h2>
            <p className="text-muted-foreground text-sm mb-4">{dialogMessage}</p>
            <Button onClick={() => setDialogOpen(false)} className="w-full">
              Close
            </Button>
          </div>
        )}

        {dialogStatus === "error" && (
          <div className="py-6">
            <h2 className="text-lg font-semibold text-red-600 mb-2">
              {dialogMessageTitle}
            </h2>
            <p className="text-muted-foreground text-sm mb-4">{dialogMessage}</p>
            <Button
              variant="destructive"
              onClick={() => setDialogOpen(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PracticeDialog({
  practiceDialogOpen,
  setPracticeDialogOpen,
  practices,
  selectedPracticeIds,
  setSelectedPracticeIds,
  sendJsonKnowledge
}: any) {
  const toggleSelection = (id: string) => {
    setSelectedPracticeIds((prev: string[]) =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  return (
    <Dialog open={practiceDialogOpen} onOpenChange={setPracticeDialogOpen}>
      <DialogContent className="max-w-lg">
        <DialogTitle>Select Practices to Add as Knowledge</DialogTitle>

        <div className="space-y-3 max-h-96 overflow-auto mt-4">
          {practices.length === 0 && (
            <p className="text-muted-foreground text-sm text-center py-6">
              No practices found
            </p>
          )}

          {practices.map((p: any) => (
            <div
              key={p.id}
              className="p-3 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-muted transition-colors"
              onClick={() => toggleSelection(p.id)}
            >
              <div>
                <h3 className="font-semibold">{p.name}</h3>
                <p className="text-sm text-muted-foreground">
                  Des : {p.description}
                </p>
              </div>

              <input
                type="checkbox"
                checked={selectedPracticeIds.includes(p.id)}
                onChange={() => toggleSelection(p.id)}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4 space-x-2">
          <Button variant="outline" onClick={() => setPracticeDialogOpen(false)}>
            Cancel
          </Button>

          <Button
            disabled={selectedPracticeIds.length === 0}
            onClick={sendJsonKnowledge}
          >
            Add Selected
          </Button>

        </div>
      </DialogContent>
    </Dialog>
  );
}